import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Storefront, CheckCircle, XCircle, Receipt, Eye, MapPin, Phone, IdentificationCard,
  Warning, ArrowsClockwise, LockKeyOpen,
} from "@phosphor-icons/react";

export function BusinessesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewReceipt, setViewReceipt] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/businesses");
      setItems(data.businesses || []);
    } catch (e) {
      toast.error("Error cargando negocios");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const confirm = async (tx_id) => {
    if (!window.confirm("¿Confirmar este pago? La suscripción se activará 30 días.")) return;
    try {
      await api.post("/admin/businesses/confirm-payment", { transaction_id: tx_id });
      toast.success("Suscripción activada");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const suspend = async (business_id) => {
    if (!window.confirm("¿Suspender este negocio? No podrá reproducir hasta que lo reactives.")) return;
    try {
      await api.post("/admin/businesses/suspend", { business_id });
      toast.success("Negocio suspendido");
      load();
    } catch (e) { toast.error("Error"); }
  };

  const reactivate = async (business_id) => {
    try {
      await api.post("/admin/businesses/reactivate", { business_id });
      toast.success("Negocio reactivado");
      load();
    } catch (e) { toast.error("Error"); }
  };

  const clearLock = async (business_id) => {
    if (!window.confirm("¿Liberar el bloqueo de red? El próximo encendido del reproductor lo asociará a la nueva ubicación.")) return;
    try {
      await api.post("/admin/businesses/clear-lock", { business_id });
      toast.success("Bloqueo liberado");
      load();
    } catch (e) { toast.error("Error"); }
  };

  const openReceipt = async (tx_id) => {
    try {
      const { data } = await api.get(`/admin/transactions/${tx_id}/receipt`);
      setViewReceipt(data.image);
    } catch { toast.error("No hay comprobante"); }
  };

  const statusBadge = (s) => {
    const map = {
      active: { bg: "bg-[#B4F34A]/15", t: "text-[#B4F34A]", label: "Activa" },
      pending_payment: { bg: "bg-[#FFB800]/15", t: "text-[#FFB800]", label: "Esperando pago" },
      expired: { bg: "bg-white/5", t: "text-white/50", label: "Vencida" },
      suspended: { bg: "bg-[#FF4B00]/15", t: "text-[#FF4B00]", label: "Suspendida" },
    };
    const m = map[s] || map.pending_payment;
    return <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${m.bg} ${m.t}`}>{m.label}</span>;
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  if (loading) return <p className="text-white/40 text-center py-12">Cargando…</p>;

  const totalActive = items.filter(b => b.subscription_status === "active").length;
  const totalPending = items.filter(b => b.pending_tx?.status === "pending_review").length;
  const mrr = totalActive * 199;

  return (
    <div data-testid="businesses-tab">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Stat label="Total negocios" value={items.length} accent="#FFFFFF" />
        <Stat label="Suscripciones activas" value={totalActive} accent="#B4F34A" />
        <Stat label="Pagos por revisar" value={totalPending} accent="#FFB800" />
        <Stat label="Ingresos mensuales" value={`$${mrr} MXN`} accent="#FF4B00" />
      </div>

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-white/60">{items.length} negocio{items.length === 1 ? "" : "s"} registrado{items.length === 1 ? "" : "s"}</p>
        <button onClick={load} className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 flex items-center gap-1.5">
          <ArrowsClockwise size={12} /> Actualizar
        </button>
      </div>

      {items.length === 0 && (
        <div className="text-center py-12 text-white/40">
          <Storefront size={40} weight="duotone" className="mx-auto mb-3 text-white/20" />
          <p>Aún no hay negocios registrados.</p>
        </div>
      )}

      <div className="space-y-3">
        {items.map(b => {
          const ptx = b.pending_tx;
          return (
            <div key={b.id} className="rounded-2xl bg-[#121212] border border-white/5 p-5" data-testid={`biz-row-${b.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <p className="font-display font-bold text-lg">{b.name}</p>
                    {statusBadge(b.subscription_status)}
                    {b.locked_ip_prefix && (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#B4F34A]/10 text-[#B4F34A]/80" title={`IP locked: ${b.locked_ip_prefix}.*`}>
                        Red bloqueada
                      </span>
                    )}
                    {b.blocked_reason && (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#FF4B00]/15 text-[#FF4B00]" title="Intento de uso desde otra red">
                        <Warning size={10} weight="fill" className="inline mr-1" /> Intento bloqueado
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-white/50 flex items-center gap-1.5"><MapPin size={12} /> {b.address}</p>
                  <p className="text-sm text-white/50 flex items-center gap-1.5"><Phone size={12} /> {b.phone}</p>
                  <p className="text-sm font-mono text-[#FFB800] flex items-center gap-1.5 mt-1">
                    <IdentificationCard size={12} weight="fill" /> {b.commerce_id}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-widest text-white/40">Vigente hasta</p>
                  <p className="text-sm font-semibold">{fmtDate(b.subscription_until)}</p>
                  <p className="text-[10px] text-white/40 mt-1">Registrado {fmtDate(b.created_at)}</p>
                </div>
              </div>

              {/* Pending payment row */}
              {ptx && (
                <div className="mt-3 rounded-xl bg-[#FFB800]/5 border border-[#FFB800]/20 p-3 flex items-center gap-3 flex-wrap">
                  <Receipt size={18} className="text-[#FFB800]" weight="fill" />
                  <div className="flex-1 min-w-[180px]">
                    <p className="text-sm font-semibold">Pago pendiente: ${ptx.amount} MXN</p>
                    <p className="text-xs text-white/50">Ref <span className="font-mono">{ptx.reference}</span> · {ptx.status === "pending_review" ? "comprobante enviado" : "esperando comprobante"}</p>
                  </div>
                  {ptx.has_receipt && (
                    <button onClick={() => openReceipt(ptx.id)} className="px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-xs flex items-center gap-1" data-testid={`biz-view-receipt-${b.id}`}>
                      <Eye size={12} /> Ver
                    </button>
                  )}
                  <button
                    onClick={() => confirm(ptx.id)}
                    className="px-3 py-1.5 rounded-full bg-[#B4F34A] hover:bg-[#A0E03A] text-black text-xs font-semibold flex items-center gap-1"
                    data-testid={`biz-confirm-${b.id}`}
                  >
                    <CheckCircle weight="fill" size={12} /> Confirmar pago
                  </button>
                </div>
              )}

              {/* Action buttons */}
              <div className="mt-3 flex gap-2 flex-wrap">
                {b.subscription_status === "suspended" ? (
                  <button onClick={() => reactivate(b.id)} className="px-3 py-1.5 rounded-full bg-[#B4F34A]/15 hover:bg-[#B4F34A]/25 text-[#B4F34A] text-xs font-semibold">
                    Reactivar
                  </button>
                ) : (
                  <button onClick={() => suspend(b.id)} className="px-3 py-1.5 rounded-full bg-white/5 hover:bg-[#FF4B00]/15 text-xs text-white/70 hover:text-[#FF4B00] flex items-center gap-1">
                    <XCircle size={12} /> Suspender
                  </button>
                )}
                {(b.locked_ip_prefix || b.blocked_reason) && (
                  <button onClick={() => clearLock(b.id)} className="px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-xs flex items-center gap-1" data-testid={`biz-clear-lock-${b.id}`}>
                    <LockKeyOpen size={12} /> Liberar bloqueo de red
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {viewReceipt && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setViewReceipt(null)}>
          <div className="max-w-2xl max-h-[90vh] overflow-auto rounded-2xl bg-[#121212] p-2" onClick={(e) => e.stopPropagation()}>
            <img src={viewReceipt} alt="Comprobante" className="w-full rounded-xl" />
            <button onClick={() => setViewReceipt(null)} className="mt-2 w-full py-3 rounded-full bg-white/5 hover:bg-white/10 text-sm">Cerrar</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-2xl bg-[#121212] border border-white/5 p-4">
      <p className="text-[10px] uppercase tracking-widest text-white/50 mb-1">{label}</p>
      <p className="font-display text-2xl font-black" style={{ color: accent }}>{value}</p>
    </div>
  );
}
